import { golfConveniosAPI } from '@/services/golfConvenios'

const state = {
  // Tipos de Convenios
  tipos: [],
  tiposActivos: [],

  // Convenios
  convenios: [],
  conveniosActivos: [],

  // Campos (Course Agreements)
  campos: [],

  // Estados
  loading: false,
  error: null,
  lastFetch: null
}

const getters = {
  // Tipos
  getTipoById: (state) => (id) =>
    state.tipos.find(t => t.id === id),

  getTiposPorDescuento: (state) => (descuento) =>
    state.tipos.filter(t => t.descuento === descuento),

  tiposCount: (state) =>
    state.tipos.length,

  // Convenios
  getConvenioById: (state) => (id) =>
    state.convenios.find(c => c.id === id),

  getConveniosPorSocio: (state) => (socioId) =>
    state.convenios.filter(c => c.socio === socioId),

  getConveniosPorCampo: (state) => (campoId) =>
    state.convenios.filter(c => c.campo === campoId),

  getConveniosPorEstado: (state) => (estado) =>
    state.convenios.filter(c => c.estado === estado),

  conveniosCount: (state) =>
    state.convenios.length,

  conveniosActivosCount: (state) =>
    state.conveniosActivos.length,

  // Campos
  getCampoById: (state) => (id) =>
    state.campos.find(c => c.id === id),

  camposCount: (state) =>
    state.campos.length,

  // Normalizar campos para compatibilidad con componentes legacy
  camposNormalizados: (state) =>
    state.campos.map(campo => ({
      id: campo.id,
      nombre: campo.name,
      ubicacion: campo.location,
      par: campo.par,
      hoyos: campo.holes,
      distancia: campo.distance,
      handicap: parseFloat(campo.handicap_rating || 0),
      horario_apertura: campo.opening_time ? campo.opening_time.substring(0, 5) : '06:00',
      horario_cierre: campo.closing_time ? campo.closing_time.substring(0, 5) : '18:00',
      email: campo.email,
      telefono: campo.phone,
      imagen: campo.image || campo.image_url || 'https://via.placeholder.com/400x200?text=' + (campo.name || 'Campo'),
      tipos_convenios: campo.agreement_types_detail ? campo.agreement_types_detail.map(t => t.nombre) : [],
      activo: campo.is_active,
      coordinador: campo.coordinator_name,
      coordinador_email: campo.coordinator_email,
      coordinador_telefono: campo.coordinator_phone,
      vigente_desde: campo.valid_from,
      vigente_hasta: campo.valid_until,
      es_vigente: campo.is_vigent,
      creado: campo.created_at,
      actualizado: campo.updated_at
    }))
}

const mutations = {
  // Tipos
  SET_TIPOS (state, tipos) {
    state.tipos = tipos
    state.lastFetch = new Date()
  },

  SET_TIPOS_ACTIVOS (state, tipos) {
    state.tiposActivos = tipos
  },

  ADD_TIPO (state, tipo) {
    state.tipos.push(tipo)
  },

  UPDATE_TIPO (state, tipo) {
    const index = state.tipos.findIndex(t => t.id === tipo.id)
    if (index >= 0) {
      state.tipos.splice(index, 1, tipo)
    }
  },

  DELETE_TIPO (state, id) {
    state.tipos = state.tipos.filter(t => t.id !== id)
    state.tiposActivos = state.tiposActivos.filter(t => t.id !== id)
  },

  // Convenios
  SET_CONVENIOS (state, convenios) {
    state.convenios = convenios
    state.lastFetch = new Date()
  },

  SET_CONVENIOS_ACTIVOS (state, convenios) {
    state.conveniosActivos = convenios
  },

  ADD_CONVENIO (state, convenio) {
    state.convenios.push(convenio)
    if (convenio.estado === 'activo') {
      state.conveniosActivos.push(convenio)
    }
  },

  UPDATE_CONVENIO (state, convenio) {
    const index = state.convenios.findIndex(c => c.id === convenio.id)
    if (index >= 0) {
      state.convenios.splice(index, 1, convenio)
      // Actualizar en activos si corresponde
      const activeIndex = state.conveniosActivos.findIndex(c => c.id === convenio.id)
      if (convenio.estado === 'activo' && activeIndex < 0) {
        state.conveniosActivos.push(convenio)
      } else if (convenio.estado !== 'activo' && activeIndex >= 0) {
        state.conveniosActivos.splice(activeIndex, 1)
      }
    }
  },

  DELETE_CONVENIO (state, id) {
    state.convenios = state.convenios.filter(c => c.id !== id)
    state.conveniosActivos = state.conveniosActivos.filter(c => c.id !== id)
  },

  // Campos
  SET_CAMPOS (state, campos) {
    state.campos = campos
  },

  ADD_CAMPO (state, campo) {
    state.campos.push(campo)
  },

  UPDATE_CAMPO (state, campo) {
    const index = state.campos.findIndex(c => c.id === campo.id)
    if (index >= 0) {
      state.campos.splice(index, 1, campo)
    }
  },

  DELETE_CAMPO (state, id) {
    state.campos = state.campos.filter(c => c.id !== id)
  },

  // Estados generales
  SET_LOADING (state, loading) {
    state.loading = loading
  },

  SET_ERROR (state, error) {
    state.error = error
  },

  CLEAR_ERROR (state) {
    state.error = null
  }
}

const actions = {
  // ========== TIPOS DE CONVENIOS ==========

  async fetchTipos ({ commit }, params = {}) {
    commit('SET_LOADING', true)
    try {
      const response = await golfConveniosAPI.listaTipos(params)
      const tipos = response.data.results || response.data
      commit('SET_TIPOS', tipos)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching tipos:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchTiposActivos ({ commit }) {
    try {
      const response = await golfConveniosAPI.tiposActivos()
      const tipos = response.data.tipos || response.data
      commit('SET_TIPOS_ACTIVOS', tipos)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching tipos activos:', error)
    }
  },

  async getTipo ({ commit }, id) {
    try {
      const response = await golfConveniosAPI.getTipo(id)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error fetching tipo ${id}:`, error)
      return null
    }
  },

  async createTipo ({ commit }, data) {
    try {
      const response = await golfConveniosAPI.crearTipo(data)
      commit('ADD_TIPO', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error creating tipo:', error)
      throw error
    }
  },

  async updateTipo ({ commit }, { id, data }) {
    try {
      const response = await golfConveniosAPI.editarTipo(id, data)
      commit('UPDATE_TIPO', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error updating tipo ${id}:`, error)
      throw error
    }
  },

  async deleteTipo ({ commit }, id) {
    try {
      await golfConveniosAPI.eliminarTipo(id)
      commit('DELETE_TIPO', id)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error deleting tipo ${id}:`, error)
      throw error
    }
  },

  // ========== CONVENIOS ==========

  async fetchConvenios ({ commit }, params = {}) {
    commit('SET_LOADING', true)
    try {
      const response = await golfConveniosAPI.listaConvenios(params)
      const convenios = response.data.results || response.data
      commit('SET_CONVENIOS', convenios)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching convenios:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchConveniosActivos ({ commit }) {
    try {
      const response = await golfConveniosAPI.conveniosActivos()
      const convenios = response.data.convenios || response.data
      commit('SET_CONVENIOS_ACTIVOS', convenios)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching convenios activos:', error)
    }
  },

  async getConvenio ({ commit }, id) {
    try {
      const response = await golfConveniosAPI.getConvenio(id)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error fetching convenio ${id}:`, error)
      return null
    }
  },

  async createConvenio ({ commit }, data) {
    try {
      const response = await golfConveniosAPI.crearConvenio(data)
      commit('ADD_CONVENIO', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error creating convenio:', error)
      throw error
    }
  },

  async updateConvenio ({ commit }, { id, data }) {
    try {
      const response = await golfConveniosAPI.editarConvenio(id, data)
      commit('UPDATE_CONVENIO', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error updating convenio ${id}:`, error)
      throw error
    }
  },

  async cancelarConvenio ({ commit }, id) {
    try {
      const response = await golfConveniosAPI.cancelarConvenio(id)
      commit('UPDATE_CONVENIO', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error canceling convenio ${id}:`, error)
      throw error
    }
  },

  async vencerConvenio ({ commit }, id) {
    try {
      const response = await golfConveniosAPI.vencerConvenio(id)
      commit('UPDATE_CONVENIO', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error venciendo convenio ${id}:`, error)
      throw error
    }
  },

  async reactivarConvenio ({ commit }, id) {
    try {
      const response = await golfConveniosAPI.reactivarConvenio(id)
      commit('UPDATE_CONVENIO', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error reactivating convenio ${id}:`, error)
      throw error
    }
  },

  async deleteConvenio ({ commit }, id) {
    try {
      await golfConveniosAPI.eliminarConvenio(id)
      commit('DELETE_CONVENIO', id)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error deleting convenio ${id}:`, error)
      throw error
    }
  },

  // ========== CAMPOS ==========

  async fetchCampos ({ commit }, params = {}) {
    commit('SET_LOADING', true)
    try {
      const response = await golfConveniosAPI.listaCampos(params)
      const campos = response.data.results || response.data
      commit('SET_CAMPOS', campos)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching campos:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getCampo ({ commit }, id) {
    try {
      const response = await golfConveniosAPI.getCampo(id)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error fetching campo ${id}:`, error)
      return null
    }
  },

  async createCampo ({ commit }, data) {
    try {
      const response = await golfConveniosAPI.crearCampo(data)
      commit('ADD_CAMPO', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error creating campo:', error)
      throw error
    }
  },

  async updateCampo ({ commit }, { id, data }) {
    try {
      const response = await golfConveniosAPI.editarCampo(id, data)
      commit('UPDATE_CAMPO', response.data)
      commit('CLEAR_ERROR')
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error updating campo ${id}:`, error)
      throw error
    }
  },

  async deleteCampo ({ commit }, id) {
    try {
      await golfConveniosAPI.eliminarCampo(id)
      commit('DELETE_CAMPO', id)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error deleting campo ${id}:`, error)
      throw error
    }
  },

  async fetchCamposActivos ({ commit }) {
    try {
      const response = await golfConveniosAPI.camposActivos()
      const campos = response.data.agreements || response.data
      commit('SET_CAMPOS', campos)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching campos activos:', error)
    }
  },

  async fetchCamposPorClub ({ commit }, housingUnitId) {
    try {
      const response = await golfConveniosAPI.camposPorClub(housingUnitId)
      const campos = response.data.campos || response.data
      commit('SET_CAMPOS', campos)
      commit('CLEAR_ERROR')
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error(`Error fetching campos para club ${housingUnitId}:`, error)
    }
  },

  // ========== GENERAL ==========

  clearError ({ commit }) {
    commit('CLEAR_ERROR')
  },

  reset ({ commit }) {
    commit('SET_TIPOS', [])
    commit('SET_TIPOS_ACTIVOS', [])
    commit('SET_CONVENIOS', [])
    commit('SET_CONVENIOS_ACTIVOS', [])
    commit('SET_CAMPOS', [])
    commit('CLEAR_ERROR')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
